import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Search, LogOut, User as UserIcon, Command,
  MessageSquare, Settings as SettingsIcon, LifeBuoy, Sun, Moon, Menu,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { ROLE_LABELS } from "@/lib/format";

export function Topbar({ onOpenCommand, onOpenMobileMenu, org }) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [alertCount, setAlertCount] = useState(0);
  const [msgCount, setMsgCount] = useState(0);
  const [mailNotifications, setMailNotifications] = useState([]);
  const [mailNotificationsLoading, setMailNotificationsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      api.get("/alerts/summary")
        .then((r) => { if (mounted) setAlertCount(r.data.unread || 0); })
        .catch(() => {});
      api.get("/caixa-postal/notifications?limit=20")
        .then((r) => {
          if (!mounted) return;
          setMsgCount(r.data?.unread || 0);
          // CAIXA_POSTAL_NOTIFICATION_ACTIVE_LIST_V4
          // O backend já retorna apenas notificações não resolvidas.
          //
          // seen_at controla SOMENTE o badge/"nova".
          // resolved_at controla permanência no dropdown.
          setMailNotifications(
            r.data?.items || []
          );
        })
        .catch(() => {
          if (!mounted) return;
          setMsgCount(0);
          setMailNotifications([]);
        });
    };
    refresh();
    const t = setInterval(refresh, 45000);
    const bumpAlert = () => setAlertCount((n) => n + 1);
    const bumpMsg = () => refresh();
    window.addEventListener("simpler:alert.created", bumpAlert);
    window.addEventListener("simpler:sync.completed", bumpMsg);
    return () => {
      mounted = false;
      clearInterval(t);
      window.removeEventListener("simpler:alert.created", bumpAlert);
      window.removeEventListener("simpler:sync.completed", bumpMsg);
    };
  }, []);

  const loadMailNotifications = useCallback(async () => {
    setMailNotificationsLoading(true);

    try {
      const response = await api.get(
        "/caixa-postal/notifications?limit=20"
      );

      setMsgCount(response.data?.unread || 0);
      // CAIXA_POSTAL_NOTIFICATION_ACTIVE_LIST_V4
      // Mantém notificações vistas na lista enquanto não
      // tiverem sido resolvidas pela leitura explícita.
      setMailNotifications(
        response.data?.items || []
      );
    } catch (error) {
      console.error(
        "Falha ao carregar notificações da Caixa Postal",
        error
      );
    } finally {
      setMailNotificationsLoading(false);
    }
  }, []);

  const openMailNotification = async (notification) => {
    if (!notification?.message_id) return;

    // MAIL_NOTIFICATION_SEEN_V3
    // Abrir a notificação significa "vista", não "mensagem lida".
    if (!notification.seen_at) {
      const now = new Date().toISOString();

      setMsgCount((current) => Math.max(0, current - 1));

      setMailNotifications((current) =>
        current.map((item) =>
          item.message_id === notification.message_id
            ? { ...item, seen_at: now }
            : item
        )
      );

      try {
        await api.post(
          `/caixa-postal/notifications/${encodeURIComponent(
            notification.message_id
          )}/seen`
        );
      } catch (error) {
        console.error(
          "Falha ao marcar notificação como vista",
          error
        );

        loadMailNotifications?.();
      }
    }

    nav(
      `/app/mensagens?message=${encodeURIComponent(
        notification.message_id
      )}`
    );
  };

  const markAllMailNotificationsSeen = async () => {
    try {
      await api.post(
        "/caixa-postal/notifications/mark-all-seen"
      );

      const now = new Date().toISOString();

      setMsgCount(0);

      // MAIL_NOTIFICATION_MARK_ALL_SEEN_V3
      // Permanecem na lista até a mensagem ser marcada como lida.
      setMailNotifications((current) =>
        current.map((item) => ({
          ...item,
          seen_at: item.seen_at || now,
        }))
      );
    } catch (error) {
      console.error(
        "Falha ao marcar notificações como vistas",
        error
      );
    }
  };

  // CAIXA_POSTAL_NOTIFICATION_REFRESH_EVENT_V1
  // Permite que a Central de Mensagens atualize o badge imediatamente
  // depois de marcar uma mensagem como lida/não lida.
  useEffect(() => {
    const refresh = () => {
      loadMailNotifications();
    };

    window.addEventListener(
      "simpler:caixa-postal-notifications-refresh",
      refresh
    );

    return () => {
      window.removeEventListener(
        "simpler:caixa-postal-notifications-refresh",
        refresh
      );
    };
  }, [loadMailNotifications]);

  const initials = (user?.name || "")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 bg-background/75 px-3 pt-3 backdrop-blur-xl md:px-5"
    >
      <div className="
          mx-auto
          flex
          min-h-[58px]
          w-full
          max-w-[1560px]
          items-center
          gap-2.5
          rounded-[18px]
          border
          border-border/70
          bg-card/95
          px-3
          shadow-[0_8px_32px_rgba(15,23,42,0.06)]
          md:px-4
        ">
        {/* Hamburger (mobile only) */}
        {onOpenMobileMenu && (
          <button
            data-testid="mobile-menu-btn"
            onClick={onOpenMobileMenu}
            className="lg:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
            title="Menu"
          >
            <Menu className="h-4 w-4" />
          </button>
        )}

        {/* Busca global */}
        <button
          data-testid="global-search-btn"
          onClick={onOpenCommand}
          className="flex h-10 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-border/70 bg-background/70 px-3.5 text-sm text-muted-foreground transition-all hover:border-primary/30 hover:bg-background hover:text-foreground hover:shadow-sm lg:flex-none lg:w-72 xl:w-[390px]"
        >
          <Search className="h-4 w-4 shrink-0 text-primary/80" />
          <span className="flex-1 text-left truncate">
            <span className="hidden sm:inline">Buscar empresas, CNPJ, certidões, obrigações…</span>
            <span className="sm:hidden">Buscar…</span>
          </span>
          <kbd className="hidden rounded-md border border-border/70 bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">⌘K</kbd>
        </button>

        <div className="flex-1" />

        {/* Seletor / info do escritório */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="org-selector-btn"
              className="hidden h-10 max-w-[230px] items-center gap-2 rounded-xl border border-border/70 bg-background/70 pl-2 pr-3 transition-all hover:border-primary/30 hover:bg-background hover:shadow-sm md:flex"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 font-display text-[11px] font-semibold text-primary">
                {(org?.name || "S").charAt(0).toUpperCase()}
              </span>
              <div className="flex flex-col items-start leading-tight min-w-0">
                <span
                  className="text-xs font-medium truncate max-w-[140px]"
                  data-testid="topbar-org-name"
                >
                  {org?.name || "—"}
                </span>
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                  Escritório
                </span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={10}
            className="
              w-64
              rounded-[18px]
              border border-border/70
              bg-popover/95
              p-2
              shadow-[0_18px_50px_rgba(15,23,42,0.10)]
              backdrop-blur-xl
            "
          >
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-medium truncate">{org?.name || "—"}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {org?.cnpj || "CNPJ não informado"}
                </span>
                {org?.cnpj_limit != null && (
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    Limite de CNPJs: {org.cnpj_limit}
                  </span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1 bg-border/60" />
            <DropdownMenuItem
              data-testid="org-goto-config"
              onClick={() => nav("/app/configuracoes")}
            >
              <SettingsIcon className="h-4 w-4 mr-2" /> Configurações do escritório
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Mensagens / notificações Caixa Postal */}
        <DropdownMenu
          onOpenChange={(open) => {
            if (open) {
              loadMailNotifications();
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <button
              data-testid="messages-btn"
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
              title="Caixa Postal Fiscal"
            >
              <MessageSquare className="h-4 w-4" />

              {msgCount > 0 && (
                <span
                  data-testid="msg-badge"
                  className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground shadow-sm"
                >
                  {msgCount > 99 ? "99+" : msgCount}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            sideOffset={10}
            className="w-[390px] max-w-[calc(100vw-24px)] rounded-[18px] border border-border/70 bg-popover/95 p-0 shadow-[0_18px_50px_rgba(15,23,42,0.10)] backdrop-blur-xl"
          >
            <div className="flex items-center justify-between border-b border-border/60 px-4 py-3.5">
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Caixa Postal
                </div>

                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {msgCount > 0
                    ? `${msgCount} nova${msgCount === 1 ? "" : "s"} notificação${msgCount === 1 ? "" : "ões"}`
                    : "Nenhuma notificação nova"}
                </div>
              </div>

              {msgCount > 0 && (
                <button
                  type="button"
                  onClick={markAllMailNotificationsSeen}
                  className="text-[11px] font-medium text-primary"
                >
                  Marcar como vistas
                </button>
              )}
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {mailNotificationsLoading ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Carregando notificações...
                </div>
              ) : mailNotifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <MessageSquare className="mx-auto h-5 w-5 text-muted-foreground/60" />

                  <div className="mt-2 text-sm font-medium text-foreground">
                    Tudo em dia
                  </div>

                  <div className="mt-1 text-xs text-muted-foreground">
                    Novas mensagens da Receita aparecerão aqui.
                  </div>
                </div>
              ) : (
                mailNotifications.map((notification) => {
                  const isNew = !notification.seen_at;

                  return (
                    <button
                      key={`${notification.user_id}-${notification.message_id}`}
                      type="button"
                      onClick={() =>
                        openMailNotification(notification)
                      }
                      className={`flex w-full gap-3 border-b border-border/50 px-4 py-3.5 text-left transition-colors last:border-b-0 ${
                        isNew
                          ? "bg-primary/[0.045]"
                          : "bg-transparent"
                      }`}
                    >
                      <span
                        className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                          isNew
                            ? "bg-primary"
                            : "bg-transparent"
                        }`}
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <span className="truncate text-[12px] font-semibold text-foreground">
                            {notification.company_name ||
                              "Empresa"}
                          </span>

                          {notification.important && (
                            <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary">
                              Importante
                            </span>
                          )}
                        </div>

                        <div className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-foreground/80">
                          {notification.subject ||
                            "Nova mensagem da Receita Federal"}
                        </div>

                        <div className="mt-1.5 text-[10px] text-muted-foreground">
                          {notification.created_at
                            ? new Date(
                                notification.created_at
                              ).toLocaleString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <button
              type="button"
              onClick={() => nav("/app/mensagens")}
              className="flex w-full items-center justify-center border-t border-border/60 px-4 py-3 text-xs font-medium text-primary"
            >
              Ver todas as mensagens
            </button>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Alertas */}
        <button
          data-testid="alerts-btn"
          onClick={() => nav("/app/alertas")}
          className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
          title="Central de Alertas"
        >
          <Bell className="h-4 w-4" />
          {alertCount > 0 && (
            <span
              data-testid="alert-badge"
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-critical px-1 text-[10px] font-semibold text-white shadow-sm"
            >
              {alertCount > 99 ? "99+" : alertCount}
            </span>
          )}
        </button>

        {/* Quick settings */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="quick-settings-btn"
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
              title="Ajustes rápidos"
            >
              <SettingsIcon className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={10}
            className="
              w-64
              rounded-[18px]
              border border-border/70
              bg-popover/95
              p-2
              shadow-[0_18px_50px_rgba(15,23,42,0.10)]
              backdrop-blur-xl
            "
          >
            <DropdownMenuLabel className="px-3 pb-2 pt-2">
              <div className="text-[13px] font-semibold text-foreground">
                Ajustes rápidos
              </div>
              <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">
                Preferências e atalhos
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1 bg-border/60" />
            <DropdownMenuItem
              data-testid="quick-toggle-theme"
              onClick={toggleTheme}
              className="min-h-10 cursor-pointer rounded-xl px-3 text-[13px] focus:bg-primary/5 focus:text-primary"
            >
              <div className="mr-2.5 flex h-7 w-7 items-center justify-center rounded-lg bg-primary/5 text-primary">
                {theme === "dark" ? (
                  <Sun className="h-3.5 w-3.5" />
                ) : (
                  <Moon className="h-3.5 w-3.5" />
                )}
              </div>

              {theme === "dark" ? "Tema claro" : "Tema escuro"}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="quick-open-command"
              onClick={onOpenCommand}
              className="min-h-10 cursor-pointer rounded-xl px-3 text-[13px] focus:bg-primary/5 focus:text-primary"
            >
              <div className="mr-2.5 flex h-7 w-7 items-center justify-center rounded-lg bg-primary/5 text-primary">
                <Command className="h-3.5 w-3.5" />
              </div>

              Command Palette

              <span className="ml-auto rounded-md border border-border/70 bg-muted/50 px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground">
                ⌘K
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-1 bg-border/60" />
            <DropdownMenuItem
              data-testid="quick-open-config"
              onClick={() => nav("/app/configuracoes")}
              className="min-h-10 cursor-pointer rounded-xl px-3 text-[13px] focus:bg-primary/5 focus:text-primary"
            >
              <div className="mr-2.5 flex h-7 w-7 items-center justify-center rounded-lg bg-primary/5 text-primary">
                <SettingsIcon className="h-3.5 w-3.5" />
              </div>

              Configurações
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="quick-open-help"
              onClick={() => nav("/app/atendimento")}
              className="min-h-10 cursor-pointer rounded-xl px-3 text-[13px] focus:bg-primary/5 focus:text-primary"
            >
              <div className="mr-2.5 flex h-7 w-7 items-center justify-center rounded-lg bg-primary/5 text-primary">
                <LifeBuoy className="h-3.5 w-3.5" />
              </div>

              Suporte
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Perfil */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-btn"
              className="flex h-10 items-center gap-2 rounded-xl border border-border/70 bg-background/70 pl-1.5 pr-3 transition-all hover:border-primary/30 hover:bg-background hover:shadow-sm"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary font-display text-xs font-semibold text-primary-foreground shadow-sm">
                {initials || <UserIcon className="h-3.5 w-3.5" />}
              </span>
              <span className="hidden md:inline text-sm font-medium truncate max-w-[100px]">
                {user?.name}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={10}
            className="
              w-64
              rounded-[18px]
              border border-border/70
              bg-popover/95
              p-2
              shadow-[0_18px_50px_rgba(15,23,42,0.10)]
              backdrop-blur-xl
            "
          >
            <DropdownMenuLabel className="px-3 pb-3 pt-2">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-[11px] font-semibold text-primary-foreground shadow-sm">
                  {initials || <UserIcon className="h-4 w-4" />}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold text-foreground">
                    {user?.name}
                  </div>

                  <div className="mt-0.5 truncate text-[10px] text-muted-foreground">
                    {user?.email}
                  </div>

                  <div className="mt-1 text-[9px] font-medium uppercase tracking-[0.12em] text-primary/70">
                    {ROLE_LABELS[user?.role] || user?.role}
                  </div>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1 bg-border/60" />
            <DropdownMenuItem
              data-testid="menu-configuracoes"
              onClick={() => nav("/app/configuracoes")}
              className="min-h-10 cursor-pointer rounded-xl px-3 text-[13px] focus:bg-primary/5 focus:text-primary"
            >
              <div className="mr-2.5 flex h-7 w-7 items-center justify-center rounded-lg bg-primary/5 text-primary">
                <UserIcon className="h-3.5 w-3.5" />
              </div>

              Meu perfil e escritório
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-command"
              onClick={onOpenCommand}
              className="min-h-10 cursor-pointer rounded-xl px-3 text-[13px] focus:bg-primary/5 focus:text-primary"
            >
              <div className="mr-2.5 flex h-7 w-7 items-center justify-center rounded-lg bg-primary/5 text-primary">
                <Command className="h-3.5 w-3.5" />
              </div>

              Command Palette

              <span className="ml-auto rounded-md border border-border/70 bg-muted/50 px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground">
                ⌘K
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-1 bg-border/60" />
            <DropdownMenuItem
              data-testid="menu-logout"
              onClick={async () => { await logout(); nav("/login"); }}
              className="min-h-10 cursor-pointer rounded-xl px-3 text-[13px] text-critical focus:bg-red-50 focus:text-critical"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
